import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiClient } from "../../lib/api/client.js";
import { createUtenzaSchema, updateUtenzaSchema, type Utenza, type CreateUtenza } from "@taras/contracts";
import { useMe } from "../../hooks/useMe.js";
import { downloadCsv } from "../../lib/csv.js";

type UpdateUtenza = z.infer<typeof updateUtenzaSchema>;

const PAGE_SIZE = 50;

export function UtenzePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [q, setQ] = useState(() => searchParams.get("q") ?? "");
  const [tipo, setTipo] = useState("");
  const [attiva, setAttiva] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [openNew, setOpenNew] = useState(false);
  const [editUtenza, setEditUtenza] = useState<Utenza | null>(null);
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const canWrite = me?.ruolo !== "VISUALIZZATORE";
  const [exporting, setExporting] = useState(false);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams();
      if (q.trim()) p.set("q", q.trim());
      if (tipo) p.set("tipo", tipo);
      if (attiva !== "") p.set("attiva", attiva);
      const rows = await apiClient.get<Record<string, unknown>[]>(`/api/v1/utenze/export?${p}`);
      downloadCsv(`utenze_${new Date().toISOString().slice(0, 10)}.csv`, rows);
    } finally {
      setExporting(false);
    }
  };

  // Reset pagination when filters change
  useEffect(() => { setCursor(null); setCursorStack([]); }, [q, tipo, attiva]);

  const buildUrl = (cur: string | null) => {
    const p = new URLSearchParams();
    p.set("limit", String(PAGE_SIZE));
    if (q.trim()) p.set("q", q.trim());
    if (tipo) p.set("tipo", tipo);
    if (attiva !== "") p.set("attiva", attiva);
    if (cur) p.set("cursor", cur);
    return `/api/v1/utenze?${p}`;
  };

  const { data, isLoading, isFetching } = useQuery<{ items: Utenza[]; next_cursor: string | null }>({
    queryKey: ["utenze", { q, tipo, attiva, cursor }],
    queryFn: () => apiClient.get(buildUrl(cursor)),
    staleTime: 30_000,
  });

  const goNext = () => {
    if (!data?.next_cursor) return;
    setCursorStack((s) => [...s, cursor ?? ""]);
    setCursor(data.next_cursor);
  };
  const goPrev = () => {
    const prev = cursorStack[cursorStack.length - 1] ?? null;
    setCursorStack((s) => s.slice(0, -1));
    setCursor(prev || null);
  };

  const hasPrev = cursorStack.length > 0;
  const hasNext = !!data?.next_cursor;
  const pageNum = cursorStack.length + 1;

  // ── Crea ──
  const createForm = useForm<CreateUtenza>({
    resolver: zodResolver(createUtenzaSchema),
    defaultValues: { tipo: "DOMESTICA", attiva: true },
  });
  const createMutation = useMutation({
    mutationFn: (body: CreateUtenza) => apiClient.post<Utenza>("/api/v1/utenze/", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["utenze"] });
      createForm.reset();
      setOpenNew(false);
    },
  });

  // ── Modifica ──
  const editForm = useForm<UpdateUtenza>({ resolver: zodResolver(updateUtenzaSchema) });
  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateUtenza }) =>
      apiClient.put<Utenza>(`/api/v1/utenze/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["utenze"] });
      setEditUtenza(null);
    },
  });

  const openEdit = (u: Utenza) => {
    setEditUtenza(u);
    editForm.reset({
      codice_tari: u.codice_tari,
      tipo: u.tipo,
      intestatario_nominativo: u.intestatario_nominativo,
      intestatario_cf_piva: u.intestatario_cf_piva ?? "",
      indirizzo_via: u.indirizzo_via,
      indirizzo_civico: u.indirizzo_civico,
      indirizzo_cap: u.indirizzo_cap,
      attiva: u.attiva,
    });
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900 shrink-0">Anagrafiche TARI</h2>

        <input type="search" placeholder="Cerca per nominativo, CF/PIVA, codice TARI, indirizzo…"
          value={q} onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-48 max-w-sm px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />

        <select value={tipo} onChange={(e) => setTipo(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
          <option value="">Tutti i tipi</option>
          <option value="DOMESTICA">Domestica</option>
          <option value="NON_DOMESTICA">Non domestica</option>
        </select>

        <select value={attiva} onChange={(e) => setAttiva(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
          <option value="">Tutti gli stati</option>
          <option value="true">Attiva</option>
          <option value="false">Cessata</option>
        </select>

        {(q || tipo || attiva) && (
          <button onClick={() => { setQ(""); setTipo(""); setAttiva(""); }}
            className="px-3 py-1.5 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
            Azzera
          </button>
        )}

        <button onClick={handleExportCsv} disabled={exporting}
          className="ml-auto px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 shrink-0 flex items-center gap-1.5">
          <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-gray-500">
            <path fillRule="evenodd" d="M2 13.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 0-1h-11a.5.5 0 0 0-.5.5ZM8 1a.5.5 0 0 1 .5.5v8.793l2.146-2.147a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 .708-.708L7.5 10.293V1.5A.5.5 0 0 1 8 1Z" clipRule="evenodd"/>
          </svg>
          {exporting ? "Esportazione…" : "Esporta CSV"}
        </button>

        {canWrite && (
          <button onClick={() => setOpenNew(true)}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shrink-0">
            Nuova utenza
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {isLoading ? (
          <div className="text-sm text-gray-500">Caricamento...</div>
        ) : (
          <>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className={th}>Codice TARI</th>
                    <th className={th}>Intestatario</th>
                    <th className={th}>CF / P.IVA</th>
                    <th className={th}>Tipo</th>
                    <th className={th}>Indirizzo</th>
                    <th className={th}>Stato</th>
                    <th className={th}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400 italic">
                        Nessuna utenza trovata.
                      </td>
                    </tr>
                  ) : data?.items.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{u.codice_tari}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {u.intestatario_nominativo || <span className="italic text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">
                        {u.intestatario_cf_piva || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                        {u.tipo === "DOMESTICA" ? "Domestica" : "Non domestica"}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {u.indirizzo_via} {u.indirizzo_civico}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${u.attiva ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {u.attiva ? "Attiva" : "Cessata"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {canWrite && (
                            <button onClick={() => openEdit(u)} title="Modifica anagrafica"
                              className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
                                <path d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13H3v-2L11.5 2.5Z" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/kit?utenza_id=${u.id}&nome=${encodeURIComponent(u.intestatario_nominativo || u.codice_tari)}`)}
                            title="Vedi kit"
                            className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
                              <rect x="2" y="3" width="12" height="8" rx="1" strokeLinejoin="round"/>
                              <path d="M5 11v2m6-2v2" strokeLinecap="round"/>
                              <circle cx="5" cy="13.5" r="0.75" fill="currentColor" stroke="none"/>
                              <circle cx="11" cy="13.5" r="0.75" fill="currentColor" stroke="none"/>
                              <path d="M6 6h4M6 8.5h2.5" strokeLinecap="round"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginazione */}
            {(hasPrev || hasNext) && (
              <div className="flex items-center justify-between">
                <button onClick={goPrev} disabled={!hasPrev || isFetching}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  ← Precedente
                </button>
                <span className="text-sm text-gray-500">
                  {isFetching ? "Caricamento…" : `Pagina ${pageNum} — ${data?.items.length ?? 0} record`}
                </span>
                <button onClick={goNext} disabled={!hasNext || isFetching}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                  Successiva →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal: Nuova utenza */}
      {openNew && (
        <ModalWrap title="Nuova utenza TARI" onClose={() => { setOpenNew(false); createForm.reset(); }}>
          <form onSubmit={createForm.handleSubmit((d) => createMutation.mutate(d))} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Codice TARI" error={createForm.formState.errors.codice_tari?.message}>
                <input {...createForm.register("codice_tari")} className={input} placeholder="TARI-001" />
              </Field>
              <Field label="Tipo" error={createForm.formState.errors.tipo?.message}>
                <select {...createForm.register("tipo")} className={input}>
                  <option value="DOMESTICA">Domestica</option>
                  <option value="NON_DOMESTICA">Non domestica</option>
                </select>
              </Field>
            </div>
            <Field label="Intestatario" error={createForm.formState.errors.intestatario_nominativo?.message}>
              <input {...createForm.register("intestatario_nominativo")} className={input} placeholder="Cognome Nome / Ragione sociale" />
            </Field>
            <Field label="C.F. / P.IVA" error={createForm.formState.errors.intestatario_cf_piva?.message}>
              <input {...createForm.register("intestatario_cf_piva")} className={input} placeholder="opzionale" />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Field label="Via" error={createForm.formState.errors.indirizzo_via?.message}>
                  <input {...createForm.register("indirizzo_via")} className={input} placeholder="Via Roma" />
                </Field>
              </div>
              <Field label="Civico" error={createForm.formState.errors.indirizzo_civico?.message}>
                <input {...createForm.register("indirizzo_civico")} className={input} placeholder="1" />
              </Field>
            </div>
            <Field label="CAP" error={createForm.formState.errors.indirizzo_cap?.message}>
              <input {...createForm.register("indirizzo_cap")} className={input} placeholder="74100" />
            </Field>
            {createMutation.isError && <p className="text-xs text-red-600">Errore durante la creazione.</p>}
            <Buttons onCancel={() => { setOpenNew(false); createForm.reset(); }} loading={createMutation.isPending} label="Crea utenza" />
          </form>
        </ModalWrap>
      )}

      {/* Modal: Modifica utenza */}
      {editUtenza && (
        <ModalWrap title="Modifica utenza" onClose={() => setEditUtenza(null)}>
          <form onSubmit={editForm.handleSubmit((d) => updateMutation.mutate({ id: editUtenza.id, body: d }))} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Codice TARI" error={editForm.formState.errors.codice_tari?.message}>
                <input {...editForm.register("codice_tari")} className={input} />
              </Field>
              <Field label="Tipo">
                <select {...editForm.register("tipo")} className={input}>
                  <option value="DOMESTICA">Domestica</option>
                  <option value="NON_DOMESTICA">Non domestica</option>
                </select>
              </Field>
            </div>
            <Field label="Intestatario" error={editForm.formState.errors.intestatario_nominativo?.message}>
              <input {...editForm.register("intestatario_nominativo")} className={input} />
            </Field>
            <Field label="C.F. / P.IVA">
              <input {...editForm.register("intestatario_cf_piva")} className={input} placeholder="opzionale" />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Field label="Via"><input {...editForm.register("indirizzo_via")} className={input} /></Field>
              </div>
              <Field label="Civico"><input {...editForm.register("indirizzo_civico")} className={input} /></Field>
            </div>
            <Field label="CAP"><input {...editForm.register("indirizzo_cap")} className={input} /></Field>
            <Field label="Stato">
              <select {...editForm.register("attiva", { setValueAs: (v) => v === "true" || v === true })} className={input}>
                <option value="true">Attiva</option>
                <option value="false">Cessata</option>
              </select>
            </Field>
            {updateMutation.isError && <p className="text-xs text-red-600">Errore durante il salvataggio.</p>}
            <Buttons onCancel={() => setEditUtenza(null)} loading={updateMutation.isPending} label="Salva" />
          </form>
        </ModalWrap>
      )}
    </div>
  );
}

// ─── Shared ───────────────────────────────────────────────────────────────────

const input = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const th = "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide";

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function Buttons({ onCancel, loading, label }: { onCancel: () => void; loading: boolean; label: string }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Annulla</button>
      <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400">
        {loading ? "Salvataggio…" : label}
      </button>
    </div>
  );
}

function ModalWrap({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}
